'use client';

import React, { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/shadcn/dialog';
import {
  useShortcuts,
  useShowHelp,
  useShortcutActions,
} from '@/shared/keyboard-shortcuts';
import { Kbd } from '@/shared/ui/shadcn/kbd';
import { cn } from '@/shared/lib/cn';

export default function KeyboardShortcutsHelp() {
  const t = useTranslations('keyboardShortcuts');
  const shortcuts = useShortcuts();
  const showHelp = useShowHelp();
  const { toggleHelp } = useShortcutActions();

  const isMac =
    typeof navigator !== 'undefined' &&
    navigator.platform.toUpperCase().indexOf('MAC') >= 0;

  const formatKey = (shortcut: (typeof shortcuts)[0]) => {
    const parts = [];

    if (shortcut.cmd || shortcut.ctrl) {
      parts.push(isMac ? '⌘' : 'Ctrl');
    }
    if (shortcut.shift) parts.push('⇧');
    if (shortcut.alt) parts.push(isMac ? '⌥' : 'Alt');

    // Format special keys
    let key = shortcut.key;
    if (key === 'Escape') key = 'Esc';
    if (key === ' ') key = 'Space';
    if (key === 'ArrowUp') key = '↑';
    if (key === 'ArrowDown') key = '↓';
    if (key === 'ArrowLeft') key = '←';
    if (key === 'ArrowRight') key = '→';
    if (key === 'Enter') key = '⏎';

    parts.push(key.toUpperCase());

    return parts.join(' + ');
  };

  // Group shortcuts by category - memoized to prevent recreation on every render
  const groupedShortcuts = useMemo(() => {
    return shortcuts.reduce(
      (acc, shortcut) => {
        const category = shortcut.global ? 'Global' : 'Page';
        if (!acc[category]) acc[category] = [];
        acc[category].push(shortcut);
        return acc;
      },
      {} as Record<string, typeof shortcuts>
    );
  }, [shortcuts]);

  return (
    <Dialog open={showHelp} onOpenChange={(open) => !open && toggleHelp()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('title')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-6">
        <div className="text-list-item-subtitle text-muted-foreground">
          {t('pressHint', { key: '?' })}
        </div>

        {Object.entries(groupedShortcuts).map(
          ([category, categoryShortcuts]) => (
            <div key={category}>
              <h3 className="text-list-item-title mb-3">
                {t('categoryShortcuts', {
                  category: category === 'Global' ? t('global') : t('page'),
                })}
              </h3>
              <div className="space-y-2">
                {categoryShortcuts.map((shortcut, index) => (
                  <div
                    key={`${shortcut.key}-${index}`}
                    className={cn(
                      'flex items-center justify-between py-2',
                      'border-b border-border last:border-0'
                    )}
                  >
                    <span className="text-list-item-subtitle">{shortcut.description}</span>
                    <Kbd className="px-3 py-1">{formatKey(shortcut)}</Kbd>
                  </div>
                ))}
              </div>
            </div>
          )
        )}

        <div
          className={cn(
            'text-card-meta text-muted-foreground pt-4',
            'border-t border-border'
          )}
        >
          {t('tip')}
        </div>
      </div>
      </DialogContent>
    </Dialog>
  );
}
